import React, { useState } from "react";
import PropTypes from "prop-types";

import _ from "lodash";
import classNames from "classnames"

import { Dropdown, Grid } from "semantic-ui-react";

import { useTranslation } from "react-i18next";


import { itemAccessors } from "../../../utils/constants";
import { round, sortAnyTypeAsc } from "../../../utils/format";
import SortByColorModal from "./SortByColor/SortByColorModal";

import "./ValueFilter.css";

// List-valued cells (e.g. batch_list) are filtered/sorted by their length rather than by content
const getFilterableValue = (row, columnId) => {
  const value = row.values[columnId];
  return Array.isArray(value) ? value.length : value;
};

const ValueFilter = ({ columnId, setFilter, preFilteredRows, filteredRows, isFilterActive }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const preFilteredKey = `${preFilteredRows.length}_${preFilteredRows[0] ? preFilteredRows[0].id : ''}`;
  const filteredKey = `${filteredRows.length}_${filteredRows[0] ? filteredRows[0].id : ''}`;

  const initialValues = React.useMemo(() => {
    if (!open) return [];
    return [...new Set(preFilteredRows.map((row) => getFilterableValue(row, columnId)))].sort(sortAnyTypeAsc);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preFilteredKey, columnId]);

  const initialValuesSet = React.useMemo(
    () => new Set(initialValues),
    [initialValues]
  );

  const filteredValues = React.useMemo(() => {
    if (!open) return [];
    if (!isFilterActive) return initialValues;
    return [...new Set(filteredRows.map((row) => getFilterableValue(row, columnId)))].sort(sortAnyTypeAsc);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filteredKey, columnId, isFilterActive, initialValues]);

  const filteredValuesSet = React.useMemo(
    () => isFilterActive ? new Set(filteredValues) : initialValuesSet,
    [isFilterActive, filteredValues, initialValuesSet]
  );

  const [localValues, setLocalValues] = useState(null);
  const setFilterDebounceRef = React.useRef(null);

  const displayValuesSet = React.useMemo(
    () => localValues !== null ? new Set(localValues) : filteredValuesSet,
    [localValues, filteredValuesSet]
  );

  const updateChecked = (option, isBoxChecked) => {
    const debounce = (fn) => {
      if (setFilterDebounceRef.current) clearTimeout(setFilterDebounceRef.current);
      setFilterDebounceRef.current = setTimeout(fn, 80);
    };

    if (option === "all" && isBoxChecked) {
      setLocalValues(initialValues);
      debounce(() => setFilter(undefined));
      return;
    } else if (option === "all" && !isBoxChecked) {
      setLocalValues([]);
      debounce(() => setFilter([]));
      return;
    }

    const currentValues = localValues ?? filteredValues;
    const newValues = isBoxChecked
      ? [...currentValues, option]
      : currentValues.filter((value) => value !== option);
    setLocalValues(newValues);
    debounce(() => setFilter(newValues));
  }


  const mapValue = (value) => {
    if (
      _.includes(
        [itemAccessors.netFlowPriority, itemAccessors.currentOnHandAlert],
        columnId
      )
    ) {
      return !_.isNaN(round(value * 100, 2))
        ? `${round(value * 100, 2)}%`
        : "-";
    } else if (columnId === itemAccessors.worstBatchStatus) {
      return t(`retail.batchStatus.${value}`);
    } else if (columnId === itemAccessors.materialMethod) {
      return t(`ordersTable.method.${value}`);
    } else if (columnId === itemAccessors.bufferZoneColor) {
      return t(`ordersTable.columns.${value}`);
    }
    return value;
  };


  if (
    columnId === itemAccessors.actualOrder ||
    columnId === itemAccessors.orderVariance ||
    columnId === itemAccessors.suggestedOrder
  ) {
    return null;
  }

  return (
    <Dropdown
      upward={false}
      icon={"filter"}
      floating
      button
      className={classNames("icon", "mini", "basic", "toggle", "order-table-column-filter", { active: isFilterActive })}
      onOpen={() => setOpen(true)}
      onClose={() => { setOpen(false); setLocalValues(null); }}
      closeOnChange={false}
      scrolling
    >
      <Dropdown.Menu className="order-table-column-filter-dropdown" onClick={(e) => e.stopPropagation()}>
        {open && (
          <>
            {columnId === itemAccessors.bufferZoneColor && <SortByColorModal/>}

            <Dropdown.Item>
              <Grid>
                <Grid.Row>
                  <div className="ui item checkbox">
                    <input
                      type="checkbox"
                      name="all"
                      onChange={(e) => {
                        updateChecked("all", e.target.checked);
                      }}
                      checked={localValues !== null ? localValues.length === initialValues.length : !isFilterActive}
                    />
                    <label>{t("ordersTable.filters.noFilter")}</label>
                  </div>
                </Grid.Row>
              </Grid>
            </Dropdown.Item>

            {initialValues.map((value) => (
              <Dropdown.Item key={value}>
                <Grid>
                  <Grid.Row>
                    <div className="ui item checkbox">
                      <input
                        type="checkbox"
                        name={value}
                        onChange={(e) => updateChecked(value, e.target.checked)}
                        checked={displayValuesSet.has(value)}
                      />
                      <label>{mapValue(value)}</label>
                    </div>
                  </Grid.Row>
                </Grid>
              </Dropdown.Item>
            ))}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

ValueFilter.propTypes = {
  columnId: PropTypes.string,
  setFilter: PropTypes.func,
  preFilteredRows: PropTypes.array,
  filteredRows: PropTypes.array,
  isFilterActive: PropTypes.bool
};

ValueFilter.defaultProps = {
  columnId: itemAccessors.code,
  setFilter: _.noop,
  preFilteredRows: [],
  filteredRows: [],
  isFilterActive: false
};

export default React.memo(ValueFilter, (prevProps, nextProps) => {
  return (
    prevProps.columnId === nextProps.columnId &&
    prevProps.isFilterActive === nextProps.isFilterActive &&
    prevProps.setFilter === nextProps.setFilter &&
    prevProps.preFilteredRows === nextProps.preFilteredRows &&
    prevProps.filteredRows === nextProps.filteredRows
  );
});
